import { Position, SectionOpenPositionsProps } from '@portfolio/models'
import { styled } from '@portfolio/styles'
import {
  Media,
  Pill,
  SectionWrapper,
  Slider,
  Link,
} from '@portfolio/components'
import { Arrow } from '@portfolio/icons'
import { useTranslation } from 'react-i18next'

const StyledSectionWrapper = styled(SectionWrapper, {
  backgroundColor: '$darkThemeBackground',
  color: '$white',

  variants: {
    isSingleCardCard: {
      true: {
        paddingInline: '$20',
      },
    },
  },
})

const Content = styled('div', {
  display: 'grid',
  gap: '$30',
  margin: '0 auto',
  containerSpace: 'medium',
  width: '100%',
  marginBottom: '$64',
})

const Title = styled('h2', {
  projectFont: 'heading02',
  textAlign: 'center',
  padding: '0 $20',
})

const Description = styled('div', {
  projectFont: 'heading07',
  textAlign: 'center',
  color: '$gray300',
  margin: '0 auto',
  maxWidth: '750px',
})

const CardWrapper = styled('div', {
  padding: '$20',
  backgroundColor: '$techstackCardsBackground',
  borderRadius: '$r3',
  width: '100%',
  margin: '0 auto',
  display: 'grid',
  gap: '$40',

  '@md': {
    padding: '$30',
  },

  variants: {
    isSingleCard: {
      true: {
        padding: '$20',
        maxWidth: '920px',
        gridTemplateRows: '1fr 1fr',
        gridTemplateColumns: '1fr',

        '@md': {
          gridTemplateColumns: '2fr 1fr',
          gridTemplateRows: '1fr',
        },
      },
    },
  },
})

const CardContentWrapper = styled('div', {
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
  gap: '$40',

  variants: {
    isSingleCard: {
      true: {
        minWidth: '280px',
      },
    },
  },
})

const UpperContent = styled('div', {
  display: 'grid',
  gridAutoFlow: 'row',
  gap: '$32',
})

const CardTitle = styled('div', {
  projectFont: 'heading07',
  fontSize: '24px',
})

const CardDescription = styled('div', {
  projectFont: 'heading08',
  color: '$white_07',
})

const ImageWrapper = styled('div', {
  borderRadius: '$r2',
  overflow: 'hidden',
})

const Pills = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  listStyleType: 'none',
  gap: '$8',
})

const StyledLink = styled(Link, {
  display: 'flex',
  alignSelf: 'flex-end',
  width: 'fit-content',
  gap: '$8',
})

const Card = ({
  _key,
  title,
  description,
  tags,
  image,
  ctaLink,
  isSingleCard = false,
}: Position & { isSingleCard?: boolean }) => {
  const { t } = useTranslation('contact')

  return (
    <CardWrapper isSingleCard={isSingleCard}>
      {isSingleCard && image ? (
        <ImageWrapper>
          <Media {...image} layout="fill" />
        </ImageWrapper>
      ) : null}
      <CardContentWrapper isSingleCard={isSingleCard}>
        <UpperContent>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>

          {tags?.length && (
            <Pills>
              {tags?.map((tag) => (
                <Pill key={`${_key}-${tag}`} appearance="UI06Uppercase">
                  {tag}
                </Pill>
              ))}
            </Pills>
          )}
        </UpperContent>

        <StyledLink {...ctaLink} appearance="containedTransparent">
          {t('applyNow')}
          <Arrow />
        </StyledLink>
      </CardContentWrapper>
    </CardWrapper>
  )
}

export const SectionOpenPositions = ({
  title,
  description,
  anchorId,
  positions,
  spacing,
  topBorder,
}: SectionOpenPositionsProps) => {
  if (!positions?.length) return null

  const isSingleCardPosition = positions?.length === 1

  return (
    <StyledSectionWrapper
      id={anchorId}
      type={spacing || 'MEDIUM'}
      isSingleCardCard={isSingleCardPosition}
      topBorder={topBorder}
    >
      <Content>
        {title && <Title>{title}</Title>}
        {description && <Description>{description}</Description>}
      </Content>

      {isSingleCardPosition ? (
        <Card key={positions[0]._key} {...positions[0]} isSingleCard />
      ) : (
        <Slider>
          {positions?.map((position) => (
            <Card key={position._key} {...position} />
          ))}
        </Slider>
      )}
    </StyledSectionWrapper>
  )
}
